import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { Icon, IconName, categoryIconName } from '../components/Icon';
import { api } from '../api/client';
import { theme } from '../theme';
import { fmtK, fmtMoney, fmtPct } from '../utils/format';
import { ApiError, Goal, GoalTier } from '../api/types';
import { checkAndCelebrate, initCelebratedFromGoals, MILESTONES } from '../utils/milestones';
import { celebrateBig, celebrateSmall } from '../utils/confetti';
import { getWindow, isInWindow } from '../utils/horizons';
import { avgMonthlyExpense } from '../utils/foundation';
import { fileToDataUrl } from '../utils/image';
import { monthKeyOf, prevMonthKey } from '../utils/game';
import { imageCache, useGoalImage } from '../hooks/useGoalImage';
import { FOUNDATION_SHARE, suggestAllocation } from '../utils/allocation';

const RU_MON = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/** Число без валюты: «12 000». */
const fmtNum = (n: number) => fmtMoney(n, '').trim();
const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

export function Goals() {
  const { viewData: data, refresh, addGameEvent } = useBudget();
  const toast = useToast();
  const currency = data?.settings.currency ?? '₽';
  const [adding, setAdding] = useState<GoalTier | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [openDream, setOpenDream] = useState<string | null>(null);
  /** Завершённый ритуал: панель со штампом «распределено» вместо ступеней. */
  const [distributed, setDistributed] = useState<{ label: string; parts: DistributedPart[] } | null>(null);

  useEffect(() => {
    if (data?.goals) initCelebratedFromGoals(data.goals);
  }, [data?.goals]);

  const monthRemainder = useMemo(() => {
    if (!data) return 0;
    const win = getWindow(1, { statsSince: data.settings.stats_since });
    let income = 0;
    let expense = 0;
    for (const op of data.operations) {
      if (!isInWindow(op.date, win)) continue;
      if (op.type === 'Доход') income += op.amount;
      else if (op.type === 'Расход') expense += op.amount;
    }
    return income - expense;
  }, [data]);

  // Ролловер: предлагаем распределить остаток ПРОШЛОГО месяца, пока он не
  // распределён — без дедлайна (пропуск ничего не отнимает, GAME_LAYER §1).
  const prevKey = prevMonthKey(new Date());
  const rolloverPending =
    !(data?.game_events ?? []).some((e) => e.type === 'month_distributed' && e.week === prevKey);

  const prevRemainder = useMemo(() => {
    if (!data) return 0;
    const since = data.settings.stats_since;
    let income = 0;
    let expense = 0;
    for (const op of data.operations) {
      if (!op.date.startsWith(prevKey)) continue;
      if (since && op.date < since) continue;
      if (op.type === 'Доход') income += op.amount;
      else if (op.type === 'Расход') expense += op.amount;
    }
    return income - expense;
  }, [data, prevKey]);

  const showRollover = rolloverPending && prevRemainder > 0;

  async function markDistributed() {
    // через очередь: офлайн-ролловер доедет при синке (сервер идемпотентен)
    await addGameEvent({
      id: `dist_${prevKey}`,
      type: 'month_distributed',
      month: monthKeyOf(new Date()),
      week: prevKey,
      coins: 0,
    });
  }

  const avgExpense = useMemo(() => {
    if (!data) return 0;
    // aggregates — серверные суммы по месяцам; у старого снапшота из IDB их нет,
    // тогда avgMonthlyExpense считает по операциям (результат тот же)
    return avgMonthlyExpense(data.operations, data.categories, data.settings, new Date(), data.aggregates);
  }, [data]);

  const goals = data?.goals ?? [];
  const dreams = goals.filter((g) => g.tier === 'dream');
  const needs = goals.filter((g) => g.tier === 'need');
  const foundation = goals.filter((g) => g.tier === 'foundation');
  const openGoals = goals.filter((g) => g.remaining > 0);

  async function delGoal(g: Goal) {
    try {
      await api.deleteGoal(g.name);
      toast.show('Цель удалена');
      setEditing(null);
      await refresh();
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    }
  }

  const editingGoal = editing ? goals.find((g) => g.name === editing) ?? null : null;

  return (
    <div style={{ padding: '26px 20px 100px' }}>
      <div
        style={{
          fontSize: 12,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: theme.colors.sub,
        }}
      >
        Мечты · нужное · подушка
      </div>
      <h1 style={{ margin: '4px 0 18px', fontSize: 27, fontWeight: 400 }}>Карта желаний</h1>

      {distributed ? (
        <DistributedStamp label={distributed.label} parts={distributed.parts} />
      ) : openGoals.length > 0 && showRollover ? (
        <DistributePanel
          goals={openGoals}
          remainder={prevRemainder}
          currency={currency}
          label={`Остаток ${MONTHS_GEN[Number(prevKey.slice(5, 7)) - 1]}`}
          onApplied={markDistributed}
          onSkip={markDistributed}
          onDistributed={(label, parts) => setDistributed({ label, parts })}
        />
      ) : openGoals.length > 0 && !showRollover ? (
        <DistributePanel
          goals={openGoals}
          remainder={monthRemainder}
          currency={currency}
          label="Свободный остаток месяца"
          onDistributed={(label, parts) => setDistributed({ label, parts })}
        />
      ) : null}

      {/* Редактор / создание */}
      {adding !== null && (
        <GoalForm
          mode="create"
          presetTier={adding}
          presetNeed={adding === 'foundation' ? Math.round((avgExpense * 6) / 1000) * 1000 : undefined}
          onCancel={() => setAdding(null)}
          onSaved={async () => {
            setAdding(null);
            await refresh();
          }}
        />
      )}
      {editingGoal && (
        <GoalForm
          mode="edit"
          goal={editingGoal}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
          onDelete={() => delGoal(editingGoal)}
        />
      )}

      {adding === null && !editingGoal && (
        <>
          <SectionTitle icon="star">Мечты</SectionTitle>
          {dreams.length > 0 && (
            <div style={{ columns: 2, columnGap: 10, marginBottom: 10 }}>
              {dreams.map((g) => (
                <DreamCard key={g.name} goal={g} onTap={() => setOpenDream(g.name)} />
              ))}
            </div>
          )}
          <button onClick={() => setAdding('dream')} style={addBtnStyle}>
            + Добавить мечту
          </button>

          <SectionTitle icon="tag">Нужное</SectionTitle>
          <div style={{ marginBottom: 10 }}>
            {needs.map((g) => (
              <NeedRow key={g.name} goal={g} onTap={() => setEditing(g.name)} />
            ))}
          </div>
          <button onClick={() => setAdding('need')} style={addBtnStyle}>
            + Добавить нужное
          </button>

          <SectionTitle icon="shield">Фундамент</SectionTitle>
          {foundation.map((g) => (
            <FoundationCard
              key={g.name}
              goal={g}
              avgExpense={avgExpense}
              currency={currency}
              onTap={() => setEditing(g.name)}
            />
          ))}
          {foundation.length === 0 && (
            <button onClick={() => setAdding('foundation')} style={addBtnStyle}>
              <Icon name="shield" size={15} inline /> Создать подушку безопасности
            </button>
          )}
        </>
      )}

      {openDream && (() => {
        const g = goals.find((x) => x.name === openDream);
        return g ? (
          <DreamDetail
            goal={g}
            currency={currency}
            onClose={() => setOpenDream(null)}
            onEdit={() => { setOpenDream(null); setEditing(g.name); }}
          />
        ) : null;
      })()}
    </div>
  );
}

function SectionTitle({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        color: theme.colors.sub,
        margin: '24px 0 10px',
        paddingLeft: 4,
        display: 'flex',
        alignItems: 'center',
        gap: 7,
      }}
    >
      <Icon name={icon} size={14} color={theme.colors.sub} />
      {children}
    </div>
  );
}

function forecastLabel(g: Goal): string | null {
  if (g.remaining <= 0) return 'готово';
  if (!g.monthly_plan || g.monthly_plan <= 0) return null;
  const months = Math.ceil(g.remaining / g.monthly_plan);
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return `к ${RU_MON[d.getMonth()]} ${d.getFullYear()}`;
}

// ============================================================================
// Карточки
// ============================================================================

const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='90' height='90'%3E%3Cfilter id='g'%3E%3CfeTurbulence baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E\")";

const ASPECTS = ['3 / 4', '4 / 5', '1 / 1', '5 / 6'];

/** Карточка мечты: фото «проявляется» из зерна по мере накопления. */
function DreamCard({ goal, onTap }: { goal: Goal; onTap: () => void }) {
  const img = useGoalImage(goal.name, !!goal.has_image);
  const pct = goal.need > 0 ? Math.min(1, goal.have / goal.need) : 0;
  const aspect = ASPECTS[hashName(goal.name) % ASPECTS.length];
  const veil = (1 - pct) * 0.78; // плотность «непроявленного» зерна

  return (
    <div
      onClick={onTap}
      style={{
        position: 'relative',
        breakInside: 'avoid',
        marginBottom: 10,
        borderRadius: theme.radius.sm,
        overflow: 'hidden',
        aspectRatio: aspect,
        background: theme.colors.incomeBg,
        border: `1px solid ${theme.colors.ink}`,
        cursor: 'pointer',
      }}
    >
      {img ? (
        <img
          src={img}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: `grayscale(${Math.round((1 - pct) * 80)}%) contrast(${1 + (1 - pct) * 0.1})`,
          }}
        />
      ) : (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.4 }}>
          <Icon name="star" size={28} color="#E4E1D8" />
        </div>
      )}
      {/* зерно проявки */}
      {img && veil > 0.02 && (
        <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: veil, mixBlendMode: 'overlay' }} />
      )}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,.7))' }} />
      {!!goal.is_shared && (
        <div style={{ position: 'absolute', top: 8, right: 8, opacity: 0.85 }}>
          <Icon name="transfer" size={14} color={theme.colors.paper} />
        </div>
      )}
      <div style={{ position: 'absolute', left: 10, right: 10, bottom: 9, color: theme.colors.paper }}>
        <div style={{ fontSize: 14 }}>{goal.name}</div>
        <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>{fmtPct(pct)} · {fmtK(goal.have)}/{fmtK(goal.need)}</div>
      </div>
      {/* baseline проявки */}
      <div style={{ position: 'absolute', left: 0, bottom: 0, height: 2, width: `${pct * 100}%`, background: '#E4E1D8' }} />
    </div>
  );
}

function hashName(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
}

/** Попап-«проявка» цели: крупное фото + история накопления + прогноз + milestones. */
function DreamDetail({
  goal,
  currency,
  onClose,
  onEdit,
}: {
  goal: Goal;
  currency: string;
  onClose: () => void;
  onEdit: () => void;
}) {
  const img = useGoalImage(goal.name, !!goal.has_image);
  const pct = goal.need > 0 ? Math.min(1, goal.have / goal.need) : 0;
  const veil = (1 - pct) * 0.78;
  const forecast = forecastLabel(goal);
  const months = goal.monthly_plan > 0 && goal.remaining > 0 ? Math.ceil(goal.remaining / goal.monthly_plan) : null;
  // вехи по механике празднований (20/40/50/60/80/100), не декоративные четверти
  const pct100 = pct * 100;
  const nextMs = MILESTONES.find((m) => pct100 < m) ?? null;
  const toNext = nextMs != null ? Math.max(0, Math.ceil((goal.need * nextMs) / 100 - goal.have)) : 0;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: theme.colors.bg,
        overflowY: 'auto',
        animation: 'rise .35s cubic-bezier(.2,.7,.2,1) both',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: theme.maxWidth, margin: '0 auto', minHeight: '100%' }}>
        {/* фото-проявка */}
        <div style={{ position: 'relative', aspectRatio: '4 / 3', background: theme.colors.incomeBg, overflow: 'hidden' }}>
          {img ? (
            <img src={img} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: `grayscale(${Math.round((1 - pct) * 80)}%)` }} />
          ) : (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.4 }}>
              <Icon name="star" size={44} color="#E4E1D8" />
            </div>
          )}
          {img && veil > 0.02 && <div style={{ position: 'absolute', inset: 0, backgroundImage: GRAIN, opacity: veil, mixBlendMode: 'overlay' }} />}
          <button
            onClick={onClose}
            style={{ position: 'absolute', top: 12, right: 12, width: 34, height: 34, borderRadius: 999, border: 'none', background: 'rgba(20,19,15,.55)', color: theme.colors.paper, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Icon name="close" size={18} color={theme.colors.paper} />
          </button>
          <div style={{ position: 'absolute', left: 0, bottom: 0, height: 3, width: `${pct * 100}%`, background: '#E4E1D8' }} />
        </div>

        <div style={{ padding: '22px' }}>
          <span className="caption">Мечта{goal.is_shared ? ' · общая' : ''}</span>
          <h1 style={{ fontSize: 30, fontWeight: 400, margin: '4px 0 16px' }}>{goal.name}</h1>

          {/* крупный прогресс */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span className="num" style={{ fontSize: 40 }}>{fmtPct(pct)}</span>
            <span style={{ color: theme.colors.sub, fontSize: 15 }}>{fmtMoney(goal.have, currency)} из {fmtMoney(goal.need, currency)}</span>
          </div>

          {/* Milestone-bar: пройденные вехи мхом, следующая — тушью, будущие — subSoft */}
          <div style={{ position: 'relative', height: 8, background: theme.colors.line, margin: '16px 0 0' }}>
            <div className="bar" style={{ width: `${pct100}%`, height: '100%', background: theme.colors.green }} />
            {MILESTONES.map((m) => {
              const isNext = m === nextMs;
              const passed = pct100 >= m;
              const ext = isNext ? 7 : 4;
              return (
                <div
                  key={m}
                  style={{
                    position: 'absolute',
                    ...(m === 100 ? { right: 0 } : { left: `${m}%` }),
                    top: -ext,
                    bottom: -ext,
                    width: isNext || passed ? 2 : 1,
                    background: isNext ? theme.colors.ink : passed ? theme.colors.green : theme.colors.subSoft,
                  }}
                />
              );
            })}
          </div>
          <div style={{ fontSize: 13, color: theme.colors.sub, fontStyle: 'italic', marginTop: 10 }}>
            {nextMs != null ? (
              <>
                до следующей вехи{' '}
                <span style={{ color: theme.colors.ink, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(toNext, currency)}</span>
                {' '}— праздник на {nextMs}%
              </>
            ) : (
              <>все вехи пройдены — цель собрана</>
            )}
          </div>

          {/* спецификация */}
          <div style={{ marginTop: 20, borderTop: `1px solid ${theme.colors.line}`, paddingTop: 16, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 16px', fontSize: 14 }}>
            <span style={{ color: theme.colors.sub }}>взнос в месяц</span>
            <span className="num" style={{ textAlign: 'right' }}>{goal.monthly_plan > 0 ? fmtMoney(goal.monthly_plan, currency) : '—'}</span>
            <span style={{ color: theme.colors.sub }}>прогноз</span>
            <span style={{ textAlign: 'right' }}>{months != null ? `${months} мес · ${forecast?.replace('к ', '') ?? ''}` : 'задай взнос'}</span>
            {goal.deadline && (<><span style={{ color: theme.colors.sub }}>дедлайн</span><span style={{ textAlign: 'right' }}>{goal.deadline}</span></>)}
            {goal.why && (<><span style={{ color: theme.colors.sub }}>зачем</span><span style={{ textAlign: 'right', fontStyle: 'italic' }}>{goal.why}</span></>)}
          </div>

          <button
            onClick={onEdit}
            style={{ width: '100%', marginTop: 22, padding: '12px 0', background: theme.colors.ink, color: '#E4E1D8', border: 'none', borderRadius: theme.radius.sm, fontFamily: theme.font, fontSize: 15, cursor: 'pointer' }}
          >
            Редактировать
          </button>
        </div>
      </div>
    </div>
  );
}

/** Строка каталога «Нужное»: hairline, шкала 3px тушью с милстоун-тиками, прогноз курсивом. */
function NeedRow({ goal, onTap }: { goal: Goal; onTap: () => void }) {
  const pct100 = goal.need > 0 ? Math.min(100, (goal.have / goal.need) * 100) : 0;
  const forecast = forecastLabel(goal);
  return (
    <div
      onClick={onTap}
      style={{ borderTop: `1px solid ${theme.colors.line}`, padding: '12px 0', cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontSize: 15, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {goal.name}
          {goal.is_shared ? <Icon name="transfer" size={12} color={theme.colors.sub} /> : null}
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 14, whiteSpace: 'nowrap' }}>
          {fmtNum(goal.have)} <span style={{ color: theme.colors.sub }}>/ {fmtNum(goal.need)}</span>
        </span>
      </div>
      <div style={{ position: 'relative', height: 3, background: theme.colors.line, marginTop: 8 }}>
        <div className="bar" style={{ width: `${pct100}%`, height: '100%', background: theme.colors.ink }} />
        {MILESTONES.filter((m) => m < 100).map((m) => (
          <div
            key={m}
            style={{
              position: 'absolute',
              left: `${m}%`,
              top: -2,
              bottom: -2,
              width: pct100 >= m ? 1.5 : 1,
              background: pct100 >= m ? theme.colors.ink : theme.colors.subSoft,
            }}
          />
        ))}
      </div>
      {forecast && (
        <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic', marginTop: 6 }}>
          {forecast === 'готово' ? 'готово' : `${forecast} при ${fmtNum(goal.monthly_plan)}/мес`}
        </div>
      )}
    </div>
  );
}

function FoundationCard({
  goal,
  avgExpense,
  currency,
  onTap,
}: {
  goal: Goal;
  avgExpense: number;
  currency: string;
  onTap: () => void;
}) {
  const { updateGoal } = useBudget();
  const toast = useToast();
  const pct = goal.need > 0 ? goal.have / goal.need : 0;
  const haveMonths = avgExpense > 0 ? goal.have / avgExpense : 0;
  const needMonths = avgExpense > 0 ? goal.need / avgExpense : 0;

  async function quickSet(months: number) {
    const need = Math.max(1000, Math.round((avgExpense * months) / 1000) * 1000);
    try {
      await updateGoal({ name: goal.name, need });
      toast.show(`Подушка: цель ${months} мес (${fmtMoney(need, currency)})`);
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    }
  }

  return (
    <div
      style={{
        background: theme.colors.incomeBg,
        color: theme.colors.paper,
        borderRadius: theme.radius.lg,
        padding: 16,
      }}
    >
      <div onClick={onTap} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 15, display: 'flex', alignItems: 'center', gap: 7 }}>
            <Icon name="shield" size={15} color="#E4E1D8" /> {goal.name}
          </div>
          <div style={{ fontSize: 13, opacity: 0.8 }}>{fmtPct(pct)}</div>
        </div>
        {avgExpense > 0 && (
          <div style={{ fontSize: 22, margin: '6px 0 2px' }}>
            {haveMonths.toFixed(1)} <span style={{ fontSize: 13, opacity: 0.8 }}>из {needMonths.toFixed(1)} мес защиты</span>
          </div>
        )}
        <div
          style={{
            height: 8,
            background: 'rgba(244,241,234,.25)',
            borderRadius: 5,
            overflow: 'hidden',
            margin: '8px 0 6px',
          }}
        >
          <div
            className="bar"
            style={{
              width: `${Math.min(100, pct * 100)}%`,
              height: '100%',
              background: theme.colors.greenSoft,
              borderRadius: 5,
            }}
          />
        </div>
        <div style={{ fontSize: 12, opacity: 0.8 }}>
          {fmtK(goal.have)} / {fmtK(goal.need)} {currency} · средний расход {fmtK(avgExpense)} {currency}/мес
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        {[3, 6, 12].map((m) => (
          <button
            key={m}
            onClick={() => quickSet(m)}
            style={{
              flex: 1,
              padding: '7px 0',
              background: 'rgba(244,241,234,.12)',
              color: theme.colors.paper,
              border: '1px solid rgba(244,241,234,.3)',
              borderRadius: theme.radius.sm,
              fontFamily: theme.font,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            цель: {m} мес
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Ритуал распределения остатка (три ступени водопада видны, не в чёрном ящике)
// ============================================================================

interface DistributedPart { name: string; amount: number }

/** Штамп-подпись как у вскрытого конверта: uppercase 10px, мох, поворот −7°. */
const stampStyle: CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 14,
  transform: 'rotate(-7deg)',
  border: `1.5px solid ${theme.colors.green}`,
  color: theme.colors.green,
  fontSize: 10,
  letterSpacing: '.16em',
  textTransform: 'uppercase',
  padding: '2px 7px',
};

const stepNumStyle: CSSProperties = {
  fontFamily: theme.fontDisplay,
  fontVariationSettings: "'opsz' 144, 'WONK' 0",
  fontSize: 12,
  color: theme.colors.sub,
};

/** Состояние после ритуала: рамка мхом + штамп «распределено». */
function DistributedStamp({ label, parts }: { label: string; parts: DistributedPart[] }) {
  return (
    <div
      className="rise"
      style={{
        border: `1px solid ${theme.colors.green}`,
        borderRadius: theme.radius.sm,
        padding: '14px 16px',
        position: 'relative',
        marginBottom: 14,
      }}
    >
      <span style={stampStyle}>распределено</span>
      <div className="caption">{label}</div>
      <div style={{ fontSize: 14, marginTop: 8, color: theme.colors.green }}>
        {parts.map((p, i) => (
          <span key={p.name}>
            {i > 0 && ' · '}
            {p.name} <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNum(p.amount)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function DistributePanel({
  goals,
  remainder,
  currency,
  label,
  onApplied,
  onSkip,
  onDistributed,
}: {
  goals: Goal[];
  remainder: number;
  currency: string;
  label: string;
  /** Вызывается после успешного распределения (ролловер помечает месяц закрытым). */
  onApplied?: () => Promise<void>;
  /** «Пропустить» — пометить месяц закрытым без распределения. */
  onSkip?: () => Promise<void>;
  /** Родитель показывает штамп «распределено» вместо панели. */
  onDistributed?: (label: string, parts: DistributedPart[]) => void;
}) {
  const { updateGoal } = useBudget();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [totalStr, setTotalStr] = useState('');
  const [allocs, setAllocs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const positive = remainder > 0;
  const tierOrder: Record<GoalTier, number> = { foundation: 0, need: 1, dream: 2 };
  const sorted = [...goals].sort(
    (a, b) => tierOrder[a.tier] - tierOrder[b.tier] || (b.priority ?? 3) - (a.priority ?? 3),
  );

  // три ступени водопада suggestAllocation — видимая механика ритуала
  const total = Math.max(0, Math.round(remainder));
  const suggested = useMemo(() => suggestAllocation(goals, total), [goals, total]);
  const foundationGoals = goals.filter((g) => g.tier === 'foundation' && g.remaining > 0);
  const foundationCap = Math.min(
    Math.round(total * FOUNDATION_SHARE),
    foundationGoals.reduce((s, g) => s + g.remaining, 0),
  );
  const foundationGot = foundationGoals.reduce((s, g) => s + (suggested.get(g.name) ?? 0), 0);
  const step1 = Math.min(foundationGot, foundationCap);
  const step3 = Math.max(0, foundationGot - step1);
  const otherParts = goals
    .filter((g) => g.tier !== 'foundation')
    .map((g) => ({ name: g.name, amount: suggested.get(g.name) ?? 0 }))
    .filter((p) => p.amount > 0)
    .sort((a, b) => b.amount - a.amount);
  const step2 = otherParts.reduce((s, p) => s + p.amount, 0);
  const suggestedSum = step1 + step2 + step3;

  const step1Note =
    foundationGoals.length === 0
      ? 'подушки нет — всё в приоритеты'
      : foundationGoals.reduce((s, g) => s + g.remaining, 0) > foundationGot
        ? 'пока не заполнена'
        : 'заполнится этим взносом';
  const step2Note =
    otherParts.length > 0
      ? otherParts.slice(0, 3).map((p) => `${p.name} ${fmtNum(p.amount)}`).join(' · ') +
        (otherParts.length > 3 ? ` · ещё ${otherParts.length - 3}` : '')
      : null;

  function openPanel() {
    setTotalStr(String(total));
    setAllocs(stringify(suggestAllocation(goals, total)));
    setOpen(true);
  }

  function recalc() {
    const t = Number(totalStr);
    if (!Number.isFinite(t) || t <= 0) {
      toast.show('сумма должна быть > 0', 'error');
      return;
    }
    setAllocs(stringify(suggestAllocation(goals, Math.round(t))));
  }

  const allocated = goals.reduce((s, g) => s + (Number(allocs[g.name]) || 0), 0);

  async function applyEntries(entries: { goal: Goal; alloc: number }[]) {
    if (entries.length === 0) {
      toast.show('нечего распределять', 'error');
      return;
    }
    setBusy(true);
    try {
      let big = false;
      let small = false;
      for (const { goal, alloc } of entries) {
        await updateGoal({ name: goal.name, have: goal.have + alloc });
        const newPct = goal.need > 0 ? ((goal.have + alloc) / goal.need) * 100 : 0;
        for (const m of checkAndCelebrate(goal.name, newPct)) {
          if (m === 50 || m === 100) big = true;
          else if (MILESTONES.includes(m)) small = true;
        }
      }
      if (big) celebrateBig();
      else if (small) celebrateSmall();
      const sum = entries.reduce((s, e) => s + e.alloc, 0);
      const parts = entries.map((e) => ({ name: e.goal.name, amount: e.alloc }));
      toast.reward({
        icon: 'transfer',
        title: 'Распределено по целям',
        amount: `+${fmtMoney(sum, currency)}`,
        coin: false,
        sub: parts.slice(0, 3).map((p) => `${p.name} ${fmtNum(p.amount)}`).join(' · '),
      });
      onDistributed?.(label, parts);
      if (onApplied) await onApplied();
      setOpen(false);
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  /** Primary «Распределить» — принять ступени как есть. */
  function applySuggested() {
    void applyEntries(
      goals
        .map((g) => ({ goal: g, alloc: suggested.get(g.name) ?? 0 }))
        .filter((e) => e.alloc > 0),
    );
  }

  /** «Применить» из редактора — по ручным значениям. */
  function applyManual() {
    void applyEntries(
      goals
        .map((g) => ({ goal: g, alloc: Math.round(Number(allocs[g.name]) || 0) }))
        .filter((e) => e.alloc > 0),
    );
  }

  async function skip() {
    if (!onSkip) return;
    setBusy(true);
    try {
      await onSkip();
      toast.show('Остаток оставлен как есть');
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        border: `1px solid ${theme.colors.ink}`,
        borderRadius: theme.radius.sm,
        padding: '14px 16px',
        marginBottom: 14,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="caption">{label}</span>
        <span
          style={{
            fontFamily: theme.fontDisplay,
            fontVariationSettings: "'opsz' 144, 'WONK' 0",
            fontVariantNumeric: 'tabular-nums',
            fontSize: 24,
            color: positive ? theme.colors.green : theme.colors.accent,
          }}
        >
          {positive ? '+' : remainder < 0 ? '−' : ''}
          {fmtMoney(Math.abs(remainder), currency)}
        </span>
      </div>

      {!open && positive && (
        <>
          <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '10px 12px', alignItems: 'baseline' }}>
            <span style={stepNumStyle}>01</span>
            <div>
              <div style={{ fontSize: 14 }}>Подушка — первые {Math.round(FOUNDATION_SHARE * 100)}%</div>
              <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic' }}>{step1Note}</div>
            </div>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 15, color: step1 > 0 ? theme.colors.ink : theme.colors.sub }}>
              {fmtNum(step1)}
            </span>

            <span style={stepNumStyle}>02</span>
            <div>
              <div style={{ fontSize: 14, color: step2 > 0 ? theme.colors.ink : theme.colors.sub }}>Приоритеты по весам</div>
              {step2Note && <div style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic' }}>{step2Note}</div>}
            </div>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 15, color: step2 > 0 ? theme.colors.ink : theme.colors.sub }}>
              {fmtNum(step2)}
            </span>

            <span style={stepNumStyle}>03</span>
            <div>
              <div style={{ fontSize: 14, color: step3 > 0 ? theme.colors.ink : theme.colors.sub }}>Переполнение → обратно в подушку</div>
            </div>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 15, color: step3 > 0 ? theme.colors.ink : theme.colors.sub }}>
              {fmtNum(step3)}
            </span>
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 14, borderTop: `1px solid ${theme.colors.line}`, paddingTop: 13 }}>
            <button onClick={applySuggested} disabled={busy || suggestedSum <= 0} style={ritualPrimaryBtn}>
              {busy ? 'Распределяю…' : 'Распределить'}
            </button>
            <button onClick={openPanel} disabled={busy} style={ritualGhostBtn}>
              Изменить
            </button>
            {onSkip && (
              <button onClick={() => void skip()} disabled={busy} style={ritualGhostBtn}>
                пропустить
              </button>
            )}
          </div>
        </>
      )}

      {!open && !positive && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={openPanel} disabled={busy} style={ritualGhostBtn}>
            Изменить сумму
          </button>
          {onSkip && (
            <button onClick={() => void skip()} disabled={busy} style={ritualGhostBtn}>
              пропустить
            </button>
          )}
        </div>
      )}

      {open && (
        <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
          <Field label="Сумма к распределению">
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="number"
                inputMode="decimal"
                value={totalStr}
                onChange={(e) => setTotalStr(e.target.value)}
                style={input}
              />
              <button onClick={recalc} disabled={busy} style={secondaryBtn}>
                авто
              </button>
            </div>
            <div style={{ fontSize: 11, color: theme.colors.sub, marginTop: 4 }}>
              подушка получает первые {Math.round(FOUNDATION_SHARE * 100)}%, пока не заполнена;
              остальное — по приоритетам
            </div>
          </Field>

          {sorted.map((g) => (
            <DistributeRow
              key={g.name}
              goal={g}
              value={allocs[g.name] ?? ''}
              onChange={(v) => setAllocs({ ...allocs, [g.name]: v })}
              currency={currency}
            />
          ))}

          <div style={{ fontSize: 13, color: theme.colors.sub }}>
            Итого: {fmtMoney(allocated, currency)}
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={applyManual} disabled={busy} style={ritualPrimaryBtn}>
              {busy ? 'Распределяю…' : 'Распределить'}
            </button>
            <button onClick={() => setOpen(false)} disabled={busy} style={ritualGhostBtn}>
              Отмена
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Строка распределения: миниатюра-фото цели + «зачем» — момент Сомана-Чимы. */
function DistributeRow({
  goal,
  value,
  onChange,
  currency,
}: {
  goal: Goal;
  value: string;
  onChange: (v: string) => void;
  currency: string;
}) {
  const img = useGoalImage(goal.name, !!goal.has_image);
  const tierIcon: IconName = goal.tier === 'foundation' ? 'shield' : goal.tier === 'need' ? 'tag' : 'star';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: theme.radius.sm,
          flexShrink: 0,
          overflow: 'hidden',
          border: `1px solid ${theme.colors.line}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {img ? (
          <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <Icon name={tierIcon} size={17} color={theme.colors.sub} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {goal.name}
        </div>
        <div
          style={{
            fontSize: 11,
            color: theme.colors.sub,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontStyle: goal.why ? 'italic' : 'normal',
          }}
        >
          {goal.why || `до цели ${fmtK(goal.remaining)} ${currency}`}
        </div>
      </div>
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...input, width: 100, flex: 'none' }}
      />
    </div>
  );
}

/** Водопад: подушка → первые 50% (с ограничением), остальное по приоритетам, излишек — обратно в подушку. */
function stringify(m: Map<string, number>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of m) out[k] = String(v);
  return out;
}

// ============================================================================
// Форма цели (создание и редактирование) с мини-WOOP
// ============================================================================

const TIER_LABELS: { id: GoalTier; label: string; icon: IconName }[] = [
  { id: 'dream', label: 'Мечта', icon: 'star' },
  { id: 'need', label: 'Нужное', icon: 'tag' },
  { id: 'foundation', label: 'Подушка', icon: 'shield' },
];

function GoalForm({
  mode,
  goal,
  presetTier,
  presetNeed,
  onCancel,
  onSaved,
  onDelete,
}: {
  mode: 'create' | 'edit';
  goal?: Goal;
  presetTier?: GoalTier;
  presetNeed?: number;
  onCancel: () => void;
  onSaved: () => Promise<void>;
  onDelete?: () => void;
}) {
  const { viewData, updateGoal } = useBudget();
  const toast = useToast();

  const [name, setName] = useState(goal?.name ?? (presetTier === 'foundation' ? 'Подушка безопасности' : ''));
  const [tier, setTier] = useState<GoalTier>(goal?.tier ?? presetTier ?? 'dream');
  const [need, setNeed] = useState(String(goal?.need ?? presetNeed ?? ''));
  const [have, setHave] = useState(String(goal?.have ?? 0));
  const [deadline, setDeadline] = useState(goal?.deadline ?? '');
  const [priority, setPriority] = useState(goal?.priority ?? 3);
  const [monthlyPlan, setMonthlyPlan] = useState(String(goal?.monthly_plan ?? 0));
  const [why, setWhy] = useState(goal?.why ?? '');
  const [obstacle, setObstacle] = useState(goal?.obstacle ?? '');
  const [isShared, setIsShared] = useState(Boolean(goal?.is_shared));
  /** undefined — не менять; null — удалить; строка — новое фото. */
  const [photo, setPhoto] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const existingImg = useGoalImage(goal?.name ?? '', Boolean(goal?.has_image));
  const preview = photo !== undefined ? photo : existingImg;

  const hotCats = (viewData?.categories ?? []).filter((c) => c.is_hot);

  async function pickPhoto(file: File | null) {
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setPhoto(dataUrl);
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'не удалось прочитать фото', 'error');
    }
  }

  async function save() {
    const nm = name.trim();
    const needN = Number(need);
    const haveN = Number(have);
    const planN = Number(monthlyPlan) || 0;
    if (!nm) {
      toast.show('имя обязательно', 'error');
      return;
    }
    if (!Number.isFinite(needN) || needN <= 0) {
      toast.show('«нужно» — число > 0', 'error');
      return;
    }
    if (!Number.isFinite(haveN) || haveN < 0) {
      toast.show('«накоплено» — число ≥ 0', 'error');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'create') {
        await api.createGoal({
          name: nm,
          need: needN,
          have: haveN,
          deadline: deadline || null,
          priority,
          tier,
          why,
          obstacle,
          monthly_plan: planN,
          is_shared: isShared,
        });
        if (photo) {
          await api.setGoalImage(nm, photo);
          imageCache.set(nm, photo);
        }
        toast.show('Цель создана');
      } else if (goal) {
        const result = await updateGoal({
          name: goal.name,
          need: needN,
          have: haveN,
          deadline: deadline || null,
          priority,
          tier,
          why,
          obstacle,
          monthly_plan: planN,
          is_shared: isShared,
        });
        if (photo !== undefined) {
          await api.setGoalImage(goal.name, photo);
          imageCache.set(goal.name, photo);
        }
        toast.show(result.synced ? 'Сохранено' : 'Сохранено локально (нет сети)');

        const newPct = needN > 0 ? (haveN / needN) * 100 : 0;
        const fresh = checkAndCelebrate(goal.name, newPct);
        for (const m of fresh) {
          if (m === 50 || m === 100) {
            celebrateBig();
            toast.show(m === 100 ? `🏆 ЦЕЛЬ ДОСТИГНУТА: «${goal.name}»!` : `🎊 Полпути! 50% от «${goal.name}»`);
          } else if (MILESTONES.includes(m)) {
            celebrateSmall();
            toast.show(`🎉 ${m}% от «${goal.name}»`);
          }
        }
      }
      await onSaved();
    } catch (e) {
      toast.show(e instanceof ApiError ? e.message : 'Ошибка', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100,
        background: theme.colors.bg,
        overflowY: 'auto',
        animation: 'rise .35s cubic-bezier(.2,.7,.2,1) both',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ maxWidth: theme.maxWidth, margin: '0 auto', width: '100%', flex: 1, padding: '22px 22px 96px' }}>
        {/* шапка */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <span className="caption">{mode === 'create' ? 'Новая цель' : 'Редактирование'}</span>
            {mode === 'edit' && <h1 style={{ fontSize: 26, fontWeight: 400, margin: '4px 0 0' }}>{goal?.name}</h1>}
          </div>
          <button onClick={onCancel} style={{ width: 34, height: 34, borderRadius: 999, border: `1px solid ${theme.colors.line}`, background: 'transparent', color: theme.colors.sub, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="close" size={18} />
          </button>
        </div>

        <FormSection title="Цель">
        {mode === 'create' && (
          <Field label="Название">
            <input value={name} onChange={(e) => setName(e.target.value)} style={input} />
          </Field>
        )}

        <Field label="Ярус">
        <div style={{ display: 'flex', gap: 6 }}>
          {TIER_LABELS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTier(t.id)}
              style={{
                flex: 1,
                padding: '9px 0',
                background: tier === t.id ? theme.colors.ink : 'transparent',
                color: tier === t.id ? '#E4E1D8' : theme.colors.ink,
                border: `1px solid ${tier === t.id ? theme.colors.ink : theme.colors.line}`,
                borderRadius: theme.radius.sm,
                fontFamily: theme.font,
                fontSize: 12,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Icon name={t.icon} size={16} />
              {t.label}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Фото">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <div
            style={{
              width: 72,
              height: 90,
              borderRadius: theme.radius.sm,
              overflow: 'hidden',
              background: theme.colors.incomeBg,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {preview ? (
              <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <Icon name="star" size={26} color="#E4E1D8" />
            )}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ ...secondaryBtn, textAlign: 'center', display: 'block' }}>
              {preview ? 'Заменить' : 'Выбрать фото'}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => void pickPhoto(e.target.files?.[0] ?? null)}
              />
            </label>
            {preview && (
              <button onClick={() => setPhoto(null)} style={{ ...secondaryBtn, color: theme.colors.red }}>
                Убрать
              </button>
            )}
          </div>
        </div>
      </Field>

      </FormSection>

      <FormSection title="Деньги">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Нужно">
          <input type="number" inputMode="decimal" value={need} onChange={(e) => setNeed(e.target.value)} style={input} />
        </Field>
        <Field label="Накоплено">
          <input type="number" inputMode="decimal" value={have} onChange={(e) => setHave(e.target.value)} style={input} />
        </Field>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Взнос в месяц">
          <input
            type="number"
            inputMode="decimal"
            value={monthlyPlan}
            onChange={(e) => setMonthlyPlan(e.target.value)}
            style={input}
          />
        </Field>
        <Field label="Дедлайн">
          <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} style={input} />
        </Field>
      </div>

      </FormSection>

      <FormSection title="Смысл и срок">
      <Field label="Зачем (одно предложение себе)">
        <input
          value={why}
          onChange={(e) => setWhy(e.target.value)}
          placeholder="напр.: утро на берегу без будильника"
          style={input}
        />
      </Field>

      <Field label="Что помешает (WOOP)">
        <input
          value={obstacle}
          onChange={(e) => setObstacle(e.target.value)}
          placeholder="категория-искуситель или привычка"
          style={input}
        />
        {hotCats.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            {hotCats.map((c) => (
              <button
                key={c.name}
                onClick={() => setObstacle(c.name)}
                style={{
                  padding: '4px 10px',
                  fontSize: 12,
                  background: obstacle === c.name ? theme.colors.ink : theme.colors.bg,
                  color: obstacle === c.name ? theme.colors.paper : theme.colors.sub,
                  border: `1px solid ${theme.colors.line}`,
                  borderRadius: 999,
                  fontFamily: theme.font,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <Icon name={categoryIconName(c)} size={12} /> {c.name}
              </button>
            ))}
          </div>
        )}
      </Field>

      <Field label="Приоритет">
        <div style={{ display: 'flex', gap: 6 }}>
          {[1, 2, 3, 4, 5].map((p) => (
            <button
              key={p}
              onClick={() => setPriority(p)}
              style={{
                flex: 1,
                padding: '9px 0',
                border: `1px solid ${theme.colors.line}`,
                background: priority === p ? theme.colors.ink : theme.colors.bg,
                color: priority === p ? theme.colors.paper : theme.colors.ink,
                borderRadius: theme.radius.sm,
                cursor: 'pointer',
                fontFamily: theme.font,
              }}
            >
              {p}
            </button>
          ))}
        </div>
      </Field>

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer', marginTop: 4 }}>
        <input type="checkbox" checked={isShared} onChange={(e) => setIsShared(e.target.checked)} />
        <Icon name="transfer" size={13} inline /> общая (с Ксюшей)
      </label>
      </FormSection>

      {mode === 'edit' && onDelete && (
        <button
          onClick={() => { if (goal && confirm(`Удалить цель «${goal.name}»?`)) onDelete(); }}
          disabled={busy}
          style={{ ...secondaryBtn, color: theme.colors.red, width: '100%', marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <Icon name="trash" size={16} /> Удалить цель
        </button>
      )}
      </div>

      {/* закреплённая панель сохранения */}
      <div style={{ position: 'sticky', bottom: 0, background: theme.colors.bg, borderTop: `1px solid ${theme.colors.line}`, padding: '12px 22px calc(12px + env(safe-area-inset-bottom))' }}>
        <div style={{ maxWidth: theme.maxWidth, margin: '0 auto', display: 'flex', gap: 8 }}>
          <button onClick={onCancel} disabled={busy} style={{ ...secondaryBtn, flex: 'none', minWidth: 100 }}>Отмена</button>
          <button onClick={save} disabled={busy} style={primaryBtn}>
            {busy ? '...' : mode === 'create' ? 'Создать' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================

const input: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm,
  fontFamily: theme.font,
  fontSize: 15,
  background: theme.colors.bg,
  color: theme.colors.ink,
  outline: 'none',
  boxSizing: 'border-box',
};

const primaryBtn: CSSProperties = {
  flex: 1,
  padding: '11px 0',
  background: theme.colors.ink,
  color: theme.colors.paper,
  border: 'none',
  borderRadius: theme.radius.md,
  fontFamily: theme.font,
  fontSize: 14,
  cursor: 'pointer',
};

const secondaryBtn: CSSProperties = {
  padding: '9px 14px',
  background: 'transparent',
  color: theme.colors.sub,
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.md,
  fontFamily: theme.font,
  fontSize: 13,
  cursor: 'pointer',
};

/** Primary ритуала: тушь/paper, Fraunces (закон радиусов «Б» — radius 2). */
const ritualPrimaryBtn: CSSProperties = {
  flex: 1,
  padding: '11px 0',
  background: theme.colors.ink,
  color: theme.colors.paper,
  border: 'none',
  borderRadius: theme.radius.sm,
  fontFamily: theme.fontDisplay,
  fontSize: 15,
  cursor: 'pointer',
};

const ritualGhostBtn: CSSProperties = {
  padding: '11px 14px',
  background: 'transparent',
  color: theme.colors.sub,
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm,
  fontFamily: theme.font,
  fontSize: 13,
  cursor: 'pointer',
};

const addBtnStyle: CSSProperties = {
  width: '100%',
  padding: '11px 0',
  background: theme.colors.bg,
  border: `1px dashed ${theme.colors.line}`,
  borderRadius: theme.radius.md,
  color: theme.colors.sub,
  fontFamily: theme.font,
  fontSize: 14,
  cursor: 'pointer',
  marginBottom: 4,
};

function FormSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div className="caption" style={{ paddingBottom: 8, marginBottom: 12, borderBottom: `1px solid ${theme.colors.line}` }}>{title}</div>
      <div style={{ display: 'grid', gap: 12 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: theme.colors.sub,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}
